import React, {Component} from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  Alert,
  Button,
  Col,
  ControlLabel,
  Form,
  FormControl,
  FormGroup,
  InputGroup,
  Row,
} from 'react-bootstrap';
import DateTimePicker from 'react-datetime';
import LoadingButton from '../../../components/LoadingButton/LoadingButton';
import Time from '../../../components/Time/Time';
import moment from 'moment-timezone';
import { withTranslation } from 'react-i18next';
import { getLocaleName } from '../../../utils/date';
import { MaterialType } from '../../../constants/MaterialModels';
import HaulerInput from './HaulerInput';

class ShipInMaterial extends Component {
  constructor(props) {
    super(props);
    this.anchor = React.createRef();
    this.state = this.getInitialState();
  }

  getInitialState() {
    return {
      quantity: 0,
      binId: '',
      vendorId: '',
      truck: '',
      hauler: '',
      bolTicket: '',
      bolQuantity: 0,
      ticket: '',
      comment: '',
      deliveryTime: null,
      error: null,
      saving: false,
    };
  }

  componentDidMount () {
    this.anchor.current.scrollIntoView();
    this.mounted = true;

    const { scheduleStore, materialStore, inventoryId } = this.props;
    const { timezone } = scheduleStore;
    const material = materialStore.getMaterialByInventoryId(inventoryId);
    const md = materialStore.materialDetails[material.materialDetailId] || {};

    this.setState({
      deliveryTime: moment().tz(timezone),
      vendorId: md.vendorId || '',
    });
  }

  componentWillUnmount () {
    this.mounted = false;
  }

  onChange = field => evt => {
    this.setState({ [field]: evt.target.value });
  };

  onChangeDate = deliveryTime => {
    const { timezone } = this.props.scheduleStore;
    this.setState({
      deliveryTime: moment(deliveryTime).tz(timezone),
    });
  };

  onShipIn = () => {
    const { shipHistoryStore, scheduleStore, notifierStore, inventoryId, materialStore, t } = this.props;
    const material = materialStore.getMaterialByInventoryId(inventoryId);
    const {
      quantity,
      binId,
      deliveryTime,
      comment,
      bolTicket,
      bolQuantity,
      ticket,
      truck,
      hauler,
      vendorId,
    } = this.state;
    const { timezone } = scheduleStore;
    const { vendor } = materialStore;

    if (material.assignment === MaterialType.Weighed && !binId) {
      this.setState({
        saving: false,
        error: t('This field is required!'),
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
    });

    shipHistoryStore
      .shipIn({
        inventoryItemId: inventoryId,
        quantity,
        binIds: material.assignment === MaterialType.Weighed ? [ binId ] : [],
        bolTicket,
        bolQuantity,
        truck,
        hauler,
        ticket,
        vendor: vendor.find(v => v.id === Number(vendorId)),
        deliveryTime: moment(deliveryTime).tz(timezone).format(),
        comment,
      })
      .then(() => {
        notifierStore.showInfo({ key: 'Material inventory updated!', props: { materialCode: material.code }});
        if (this.mounted) {
          this.setState({
            ...this.getInitialState(),
            deliveryTime: moment().tz(timezone),
          });
        }
      })
      .catch(err => {
        if (this.mounted) {
          this.setState({ error: err.message, saving: false });
        }
      });
  };

  onClose = () => {
    const { timezone } = this.props.scheduleStore;
    this.setState({
      quantity: 0,
      vendorId: '',
      truck: '',
      hauler: '',
      ticket: '',
      bolTicket: '',
      bolQuantity: 0,
      comment: '',
      deliveryTime: moment().tz(timezone).toDate(),
      error: null,
      saving: false,
    });
    this.props.onClose();
  };

  renderForm(material) {
    const {
      saving,
      quantity,
      binId,
      ticket,
      bolTicket,
      bolQuantity,
      truck,
      vendorId,
      comment,
      deliveryTime,
      hauler,
      error,
    } = this.state;
    const { materialStore, devicesStore, newAuthStore, t } = this.props;
    const { vendor } = materialStore;
    const { user } = newAuthStore;
    const materialBins = [];

    if (material.bins) {
      material.bins.forEach(v => {
        const bin = devicesStore.getDeviceById(v.binId);
        if (bin) {
          const binBalance = material.deviceBalance.find(x => x.deviceId === v.binId);
          materialBins.push({ ...bin, ...binBalance });
        }
        const linkedBin = devicesStore.getDeviceById(v.linkedBinId);
        if (linkedBin) {
          const binBalance = material.deviceBalance.find(x => x.deviceId === v.linkedBinId);
          materialBins.push({ ...linkedBin, ...binBalance });
        }
      });
    }

    return(
      <Form onSubmit={e => e.preventDefault()}>
        <Row className="form-fields">
          <Col sm={12}>
            <FormGroup>
              <ControlLabel>{t('Truck')}</ControlLabel>
              <FormControl
                name="truck"
                type="text"
                value={truck}
                onChange={this.onChange('truck')}
              />
            </FormGroup>
          </Col>
          <Col sm={4}>
            <FormGroup>
              <ControlLabel>{t('Vendor')}</ControlLabel>
              <FormControl
                name="vendorId"
                componentClass="select"
                value={vendorId}
                onChange={this.onChange('vendorId')}
                bsSize="sm"
              >
                <option value=""/>
                { vendor.map((option, i) => (
                  <option key={i} value={option.id}>
                    {option.code} – {option.name}
                  </option>
                ))}
              </FormControl>
            </FormGroup>
          </Col>
          <Col sm={4}>
            <FormGroup>
              <ControlLabel>{t('Ticket')}</ControlLabel>
              <FormControl
                name="ticket"
                type="text"
                value={ticket}
                onChange={this.onChange('ticket')}
              />
            </FormGroup>
          </Col>
          <Col sm={4}>
            <ControlLabel>{t('Quantity')}</ControlLabel>
            <FormGroup>
              <InputGroup>
                <FormControl
                  name="quantity"
                  type="number"
                  value={quantity}
                  onChange={this.onChange('quantity')}
                />
                <InputGroup.Addon>
                  {material && material.itemInventoryUnitSymbol}
                </InputGroup.Addon>
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm={4}>
            <HaulerInput
              value={hauler}
              onChange={newValue => this.setState({ hauler: newValue })}
            />
          </Col>
          <Col sm={4}>
            <FormGroup>
              <ControlLabel>{t('BOL Ticket')}</ControlLabel>
              <FormControl
                name="bolTicket"
                type="text"
                value={bolTicket}
                onChange={this.onChange('bolTicket')}
              />
            </FormGroup>
          </Col>
          <Col sm={4}>
            <ControlLabel>{t('BOL Quantity')}</ControlLabel>
            <FormGroup>
              <InputGroup>
                <FormControl
                  name="bolQuantity"
                  type="number"
                  value={bolQuantity}
                  onChange={this.onChange('bolQuantity')}
                />
                <InputGroup.Addon>
                  {material && material.itemInventoryUnitSymbol}
                </InputGroup.Addon>
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm={4}>
            <FormGroup>
              <ControlLabel>{t('Delivery Time')}</ControlLabel>
              <DateTimePicker
                inputProps={{ className: 'form-control' }}
                onChange={this.onChangeDate}
                value={deliveryTime}
                locale={getLocaleName(user.language)}
              />
            </FormGroup>
          </Col>
          {material.assignment === MaterialType.Weighed && (
            <Col sm={4}>
              <ControlLabel>
                {t('Bin')}
              </ControlLabel>
              <FormGroup className={!binId && error ? 'has-error' : ''}>
                <FormControl
                  name="binId"
                  componentClass="select"
                  value={binId}
                  onChange={this.onChange('binId')}
                  bsSize="sm"
                >
                  <option/>
                  {materialBins.map((option, i) => (
                    <option key={i} value={option.id}>
                      {option.description || option.id} :: {option.currentBalance}
                    </option>
                  ))}
                </FormControl>
              </FormGroup>
            </Col>
          )}
          <Col sm={8}>
            <FormGroup>
              <ControlLabel>{t('Comment')}</ControlLabel>
              <FormControl
                name="comment"
                componentClass="textarea"
                type="text"
                value={comment}
                onChange={this.onChange('comment')}
              />
            </FormGroup>
          </Col>
        </Row>
        {!!error && <Alert bsStyle="danger">{error}</Alert>}
        <Row className="form-buttons">
          <Col sm={12} className="text-right">
            <Button onClick={this.onClose}>{t('Close')}</Button>
            {' '}
            <LoadingButton
              bsStyle="primary"
              onClick={this.onShipIn}
              label={t('Save')}
              loadingLabel={t('Save')}
              isLoading={saving}
            />
          </Col>
        </Row>
      </Form>
    );
  }

  render() {
    const { materialStore, inventoryId, t } = this.props;
    const material = materialStore.getMaterialByInventoryId(inventoryId);
    const name = material ? materialStore.getMaterialName(material) : '';

    return !material ? '' : (
      <div className="material-ship-in" ref={this.anchor}>
        <Row className="form-header">
          <Col sm={12}>
            <a className="btn-panel-close" onClick={this.onClose}>x</a>
            <h4>{t('Ship In')} - {name}</h4>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <p>
              <b>{t('Current Quantity')}: </b>
              {material && materialStore.getDevicesBalance(material)} {' '}
              {material && material.itemInventoryUnitSymbol}
              <br/>
              <b>{t('Time')}: </b> <Time />
            </p>
          </Col>
        </Row>
        {this.renderForm(material)}
      </div>
    );
  }
}

export default withTranslation()(inject('shipHistoryStore', 'scheduleStore', 'materialStore', 'notifierStore', 'devicesStore', 'newAuthStore')(observer(ShipInMaterial)));
